import { createApp, defineComponent, h, shallowRef, type Component } from "vue";
import App from "./App.vue";
import { router } from "./router";
import { authKey, createAuthProvider, installAuthGuard } from "./lib/auth";
import { apiKey, createApiClient } from "./lib/api";
import { configKey, loadAppConfig } from "./lib/config";
import {
  createCustomerSession,
  createCustomerSessionApi,
  customerSessionKey,
} from "./features/customer-session";
import { createPaymentProvider, paymentProviderKey } from "./features/payment";
import PortalUnavailableView from "./views/PortalUnavailableView.vue";
import { createI18n, getBrandMessages } from "./lib/i18n";
import { getBranding } from "./lib/branding";
import { applyTheme } from "@token/shared";
import "./style.css";

const bootView = shallowRef<Component | null>(null);

const BootstrapRoot = defineComponent({
  name: "BootstrapRoot",
  setup() {
    return () =>
      bootView.value
        ? h(bootView.value)
        : h(
            "div",
            {
              class: "fixed inset-0 flex items-center justify-center bg-background",
              role: "status",
              "aria-label": "Loading",
            },
            [h("div", { class: "h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" })],
          );
  },
});

const app = createApp(BootstrapRoot);
const i18n = createI18n();
app.use(i18n);
app.mount("#app");

async function bootstrap() {
  const config = await loadAppConfig();
  applyTheme(config.theme);
  const messages = getBrandMessages(config.branding);
  i18n.global.setLocaleMessage("de", messages.de);
  i18n.global.setLocaleMessage("en", messages.en);
  document.title = getBranding(config.branding).portalName;
  const auth = createAuthProvider(config.auth, config.apiUrl);
  const api = createApiClient({
    auth,
    baseUrl: config.apiUrl,
    apiPrefix: "/api/portal",
  });
  const customerSession = createCustomerSession({
    auth,
    api: createCustomerSessionApi(api, {
      mode: config.apiUrl ? "http" : "mock",
      mockKycStatus: import.meta.env.VITE_PORTAL_MOCK_KYC_STATUS,
    }),
  });
  installAuthGuard(router, auth, customerSession);

  const paymentProvider = createPaymentProvider(config.payment);

  app.provide(authKey, auth);
  app.provide(apiKey, api);
  app.provide(customerSessionKey, customerSession);
  app.provide(configKey, config);
  app.provide(paymentProviderKey, paymentProvider);
  app.use(router);
  bootView.value = App;
}

bootstrap().catch((err) => {
  console.error("Failed to bootstrap portal:", err);
  bootView.value = PortalUnavailableView;
});
